import { callUnaryMethod } from '@connectrpc/connect-query'
import { useQuery } from '@tanstack/react-query'

import { useAuthenticated } from '@jotta/auth-client/useAuthStatus'
import { getBackgroundJob } from '@jotta/grpc-connect-openapi/backgroundJobQuery'
import type { GetBackgroundJobResponse } from '@jotta/grpc-connect-openapi/job'
import type { BackgroundJobStatus } from '@jotta/hooks/useBackgroundJobStore'
import {
  updateBackgroundJob,
  useBackgroundJobStore,
  useHasOngoingBackgroundJobs,
} from '@jotta/hooks/useBackgroundJobStore'

import { authTransport } from './transport'

const mapStatus = (
  backgroundJobStatus: GetBackgroundJobResponse,
): BackgroundJobStatus => {
  switch (backgroundJobStatus.status.case) {
    case 'failed':
    case undefined:
      return 'FAILED'
    case 'inProgress':
      return 'IN_PROGRESS'
    case 'notFound':
      return 'NOT_FOUND'
    case 'success':
      return 'SUCCESS'
  }
}

const pollFunction = (ids: string[]) =>
  Promise.all(
    ids.map(async jobId => {
      const response = await callUnaryMethod(authTransport, getBackgroundJob, {
        jobId,
      })
      updateBackgroundJob(jobId, mapStatus(response))
    }),
  )

export const useBackgroundJobPoller = () => {
  const { backgroundJobs } = useBackgroundJobStore()
  const hasOngoingJobs = useHasOngoingBackgroundJobs()
  const authenticated = useAuthenticated()

  // Get jobs that are in progress
  const inProgressJobIds = Object.values(backgroundJobs)
    .filter(job => job.status === 'IN_PROGRESS')
    .map(job => job.id)

  useQuery({
    queryKey: ['backgroundJobs'],
    queryFn: () => pollFunction(inProgressJobIds),
    enabled: authenticated === 'TRUE' && hasOngoingJobs, // Start polling only if there are jobs
    refetchInterval: 500, // Poll every 0.5 seconds
    notifyOnChangeProps: [], // Declare we are not using the result, and don't need re-renders
  })
}
